import { HttpErrorResponse } from '@angular/common/http';

/**
 * Narrows `unknown` to a plain object record (never `null`, an array or a primitive).
 *
 * Shared with the PCS interceptor: both the body extractor below and its blob
 * decoder need the same JSON-object test.
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Shared RFC 9457 body extractor behind the DCS/PCS/PHS `*ProblemDetailsInterceptor`s.
 *
 * The library `ErrorInterceptor` rewrites any `HttpErrorResponse` into its legacy
 * `{ code: 'HTTP_ERROR', … }` payload unless it no longer recognises the error, so the
 * interceptors rethrow the ProblemDetails BODY as a plain object. Returns that body, or
 * `null` to leave the error untouched.
 *
 * Scope is deliberately narrow:
 * - 401 is never defused — it must keep the global logout/redirect flow;
 * - the guard is the TITLE SHAPE (record with a non-blank string `title`), never a code
 *   allowlist, so unknown future backend codes still defuse the raw toast; DCS envelopes,
 *   HTML 5xx pages and empty bodies pass through so the library handling keeps working.
 *
 * Sibling `isRecord` type guards live in `catalog-problem-display.ts` and `pcs-problem.ts`.
 */
export function extractProblemDetailsBody(err: unknown): Record<string, unknown> | null {
  if (!(err instanceof HttpErrorResponse) || err.status === 401) {
    return null;
  }

  const body = err.error;
  if (!isRecord(body)) {
    return null;
  }

  const title = body['title'];
  return typeof title === 'string' && title.trim().length > 0 ? body : null;
}
